import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Popover,
  Divider,
  TextField,
} from '@mui/material';
import { es } from 'date-fns/locale';
import { format, subDays, endOfMonth, subMonths } from 'date-fns';
import { chartPalette } from './chartPalette';

function toInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function fromInputValue(s: string): Date {
  const d = new Date(s);
  return isNaN(d.getTime()) ? new Date() : d;
}

export type DatePresetId =
  | 'today'
  | 'yesterday'
  | 'daysAgo7'
  | 'daysAgo30'
  | 'endOfThisMonth'
  | 'endOfLastMonth'
  | 'custom';

export interface DashboardDatePickerProps {
  value: Date;
  onChange: (date: Date) => void;
  open?: boolean;
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
  timezoneLabel?: string;
  inline?: boolean;
  trigger?: React.ReactNode;
}

const PRESETS: { id: DatePresetId; label: string }[] = [
  { id: 'today', label: 'Hoy' },
  { id: 'yesterday', label: 'Ayer' },
  { id: 'daysAgo7', label: 'Hace 7 días' },
  { id: 'daysAgo30', label: 'Hace 1 mes' },
  { id: 'endOfThisMonth', label: 'Fin de este mes' },
  { id: 'endOfLastMonth', label: 'Fin del mes pasado' },
  { id: 'custom', label: 'Personalizado' },
];

export function getDateFromPreset(presetId: DatePresetId): Date {
  const today = new Date();
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (presetId) {
    case 'today':
      return now;
    case 'yesterday':
      return subDays(now, 1);
    case 'daysAgo7':
      return subDays(now, 7);
    case 'daysAgo30':
      return subDays(now, 30);
    case 'endOfThisMonth':
      return endOfMonth(now);
    case 'endOfLastMonth':
      return endOfMonth(subMonths(now, 1));
    case 'custom':
      return now;
    default:
      return now;
  }
}

function getDisplayLabel(presetId: DatePresetId, date: Date): string {
  if (presetId === 'custom') {
    return `Personalizado: ${format(date, 'd MMM yyyy', { locale: es })}`;
  }
  const preset = PRESETS.find((p) => p.id === presetId);
  return `${preset?.label ?? presetId}: ${format(date, 'd MMM yyyy', { locale: es })}`;
}

export const DashboardDatePicker: React.FC<DashboardDatePickerProps> = ({
  value,
  onChange,
  open = false,
  anchorEl = null,
  onClose,
  timezoneLabel = 'hora local',
  inline = false,
  trigger,
}) => {
  const [presetId, setPresetId] = useState<DatePresetId>('today');
  const [customDate, setCustomDate] = useState<Date>(value);
  const [pendingDate, setPendingDate] = useState<Date>(value);

  useEffect(() => {
    if (open) {
      setPendingDate(value);
      setCustomDate(value);
      const matched = PRESETS.find((p) => {
        if (p.id === 'custom') return false;
        const presetDate = getDateFromPreset(p.id);
        return format(presetDate, 'yyyy-MM-dd') === format(value, 'yyyy-MM-dd');
      });
      setPresetId(matched?.id ?? 'custom');
    }
  }, [open, value]);

  const effectiveDate = presetId === 'custom' ? customDate : pendingDate;

  const displayLabel = useMemo(
    () => getDisplayLabel(presetId, effectiveDate),
    [presetId, effectiveDate],
  );

  const handlePresetChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newPresetId = event.target.value as DatePresetId;
    setPresetId(newPresetId);
    if (newPresetId !== 'custom') {
      const date = getDateFromPreset(newPresetId);
      setPendingDate(date);
      setCustomDate(date);
    }
  }, []);

  const handleCustomDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDate(fromInputValue(e.target.value));
  }, []);

  const handleActualizar = useCallback(() => {
    const date = presetId === 'custom' ? customDate : pendingDate;
    onChange(date);
    onClose?.();
  }, [presetId, customDate, pendingDate, onChange, onClose]);

  const handleCancelar = useCallback(() => {
    setPendingDate(value);
    setCustomDate(value);
    onClose?.();
  }, [value, onClose]);

  const content = (
    <Paper
      elevation={8}
      sx={{
        p: 2,
        minWidth: 380,
        maxWidth: 480,
        borderRadius: 2,
        background:
          'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: '#ffffff',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {displayLabel}
      </Typography>
      <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 200 }}>
          <RadioGroup value={presetId} onChange={handlePresetChange}>
            {PRESETS.map((preset) => (
              <FormControlLabel
                key={preset.id}
                value={preset.id}
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': { color: chartPalette.accent.teal },
                    }}
                  />
                }
                label={preset.label}
                sx={{
                  display: 'block',
                  mb: 0.5,
                  '& .MuiFormControlLabel-label': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '0.875rem',
                  },
                }}
              />
            ))}
          </RadioGroup>
        </Box>
        {presetId === 'custom' && (
          <Box sx={{ minWidth: 200 }}>
            <TextField
              label="Fecha"
              type="date"
              value={toInputValue(effectiveDate)}
              onChange={handleCustomDateChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                  '&.Mui-focused fieldset': { borderColor: '#0BB296' },
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: 1,
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#0CBB9A' },
              }}
            />
          </Box>
        )}
      </Box>
      <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'rgba(255, 255, 255, 0.55)',
          mb: 2,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        Las fechas se muestran en la {timezoneLabel}.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          onClick={handleCancelar}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            borderColor: 'rgba(255, 255, 255, 0.25)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.45)',
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
            },
          }}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleActualizar}
          sx={{
            background: `linear-gradient(to right, ${chartPalette.accent.cyan}, ${'#0CBB9A'})`,
            color: '#fff',
            '&:hover': {
              background: `linear-gradient(to right, ${chartPalette.accent.tealDark}, #059669)`,
            },
          }}
        >
          Actualizar
        </Button>
      </Box>
    </Paper>
  );

  if (inline) {
    return <>{content}</>;
  }

  return (
    <>
      {trigger}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancelar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            },
          },
        }}
      >
        {content}
      </Popover>
    </>
  );
};
